import { jsPDF } from 'jspdf';
import { Injectable, inject } from '@angular/core';
import { TranslationService } from '../translations/services/translation.service';
import { Store } from '@ngxs/store';
import { CustomerInfo } from '../../features/customer-info/model/customer-info';
import {
  ActionDetails,
  ActionType,
  DispositionType,
  getActionType,
  SpecificationType,
  SpecLine
} from '../../features/asset-management/models/SpecLine';
import { Limit } from '../../features/asset-management/models/Limit';
import { RerunReworkReason } from '../../features/product-data/models/rerun-rework-reason.model';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {
  customerInfo: CustomerInfo;
  public translationService = inject(TranslationService);
  public store = inject(Store);

  constructor() {}
  generatePDF(specName: string, specLines: SpecLine[], useCustomerInfo: boolean) {
    const doc = new jsPDF();
    const labelsMap = new Map<string, number>();
    for (let pageCounter = 0; pageCounter < specLines.length; pageCounter++) {
      // Create a new PDF
      this.store.select((state) => state.currentCustomer.customerInfo).subscribe((customerInfo) => (this.customerInfo = customerInfo));
      //cam specs
      let specTypeValue: string;
      let specLineTreadsValue: string;
      let treadHeaderLabel: string;
      const specLine = specLines[pageCounter];

      if (specLine.label === null) {
        if (!specLine.defaultSpec) {
          if (specLine.type === SpecificationType.REPAIR_RETREAD) {
            specTypeValue = this.translationService.translate('rrText');
            specLineTreadsValue = specLine.treads.join(', ');
          } else {
            specTypeValue = this.translationService.translate('roText');
            specLineTreadsValue = `${specLine.applicationDefinition} RO`;
          }
          if (labelsMap.has(specLineTreadsValue)) {
            const newCount = labelsMap.get(specLineTreadsValue) + 1;
            labelsMap.set(specLineTreadsValue, newCount);
            specLineTreadsValue = `${specLineTreadsValue} (${newCount})`;
          } else {
            labelsMap.set(specLineTreadsValue, 0);
            specLineTreadsValue = `${specLineTreadsValue}`;
          }
        } else {
          if (specLine.type === SpecificationType.REPAIR_RETREAD) {
            specTypeValue = this.translationService.translate('rrText');
            specLineTreadsValue = 'Default RR';
          } else {
            specTypeValue = this.translationService.translate('roText');
            specLineTreadsValue = 'Default RO';
          }
        }
      } else {
        specTypeValue =
          specLine.type === SpecificationType.REPAIR_RETREAD
            ? this.translationService.translate('rrText')
            : this.translationService.translate('roText');
        specLineTreadsValue = specLine.label;
      }

      treadHeaderLabel = specLineTreadsValue;
      // Get action details for 'ntr'
      const actionDetails = this.setActionDetails('ntr', specLine.ntr);
      const ntractionValue1 = actionDetails.actionValue1;
      const ntractionValue2 = actionDetails.actionValue2;
      const ntractionValue3 = actionDetails.actionValue3;
      const ntractionLabel1 = actionDetails.actionLabel1;
      const ntractionLabel2 = actionDetails.actionLabel2;
      const ntractionLabel3 = actionDetails.actionLabel3;

      // Get action details for 'age'
      const actionDetailsAge = this.setActionDetails('age', specLine.age);
      const ageactionValue1 = actionDetailsAge.actionValue1;
      const ageactionValue2 = actionDetailsAge.actionValue2;
      const ageactionValue3 = actionDetailsAge.actionValue3;
      const ageactionLabel1 = actionDetailsAge.actionLabel1;
      const ageactionLabel2 = actionDetailsAge.actionLabel2;
      const ageactionLabel3 = actionDetailsAge.actionLabel3;

      // Define the sections and their corresponding data points
      const sections = [
        {
          header: 'Specification Information',
          dataPoints: [
            { label: 'camSpecNameColumnText', value: 'specCode' },
            { label: 'camSpecDescriptionColumnText', value: 'specDescription' },
            { label: 'camSpecTypeText', value: 'specType' }
            // Add more data points as needed
          ]
        },
        {
          header: treadHeaderLabel,
          dataPoints: [
            { label: 'applicationText', value: 'applicationDefinition' },
            { label: 'sizesText', value: 'sizes' },
            { label: 'makesText', value: 'makes' },
            { label: 'axlesText', value: 'axles' },
            { label: 'seriesText', value: 'series' },
            { label: 'treadsText', value: 'treads' }
          ]
        },
        {
          header: 'ntrAndAgeText',
          dataPoints: [
            { label: 'ntrText', value: 'ntr.limit' },
            { label: 'actionText', value: 'ntr.action' },
            { label: ntractionLabel1, value: ntractionValue1 },
            { label: ntractionLabel2, value: ntractionValue2 },
            { label: ntractionLabel3, value: ntractionValue3 },
            { label: 'ageMonthsText', value: 'age.limit' },
            { label: 'actionText', value: 'age.action' },
            { label: ageactionLabel1, value: ageactionValue1 },
            { label: ageactionLabel2, value: ageactionValue2 },
            { label: ageactionLabel3, value: ageactionValue3 }
          ]
        }
      ];
      // doc.line(20, 20, 100, 50);
      let customerCounter = 0;
      let customerYIndex = 0;
      if (useCustomerInfo && pageCounter === 0) {
        // Loop through customer labels and values if there are customer data
        const customerInfoSection = [
          {
            header: 'customerInformationHeader',
            dataPoints: [
              { label: 'customerCodeText', value: this.customerInfo.customerCode },
              { label: 'customerNameText', value: this.customerInfo.customerName },
              { label: 'shipToText', value: this.customerInfo.shipTo },
              { label: 'customerAddress1Text', value: this.customerInfo.addressOne },
              { label: 'salespersonIdText', value: this.customerInfo.salespersonId },
              { label: 'customerAddress2Text', value: this.customerInfo.addressTwo },
              {
                label: 'customerCityStateZipText',
                value: `${this.customerInfo.city}, ${this.customerInfo.state}, ${this.customerInfo.zip}`
              }
            ]
          }
        ];
        customerYIndex = 30;
        customerInfoSection.forEach((section, sectionIndex) => {
          const header = this.translationService.translate(section.header);
          doc.text(header, 10, 10 + sectionIndex * 40);

          section.dataPoints.forEach((dataPoint, dataIndex) => {
            const label = this.translationService.translate(dataPoint.label);

            if (dataIndex % 4 === 0 && dataIndex !== 0) {
              customerCounter++;
            }
            doc.setFontSize(6);
            doc.text(label, 15 + (dataIndex % 4) * 50, 15 + customerCounter * 12 + sectionIndex * 30);
            doc.setFontSize(8);
            this.drawUnderline(dataPoint.value, 15 + (dataIndex % 4) * 50, 20 + customerCounter * 12 + sectionIndex * 30, doc, 30);
          });
        });
      }
      sections.forEach((section, sectionIndex) => {
        const header = this.translationService.translate(section.header);
        if (section.header === 'Specification Information') {
          doc.setFontSize(11);
          doc.text(header, 10, 10 + customerYIndex);
        } else if (section.header === treadHeaderLabel) {
          doc.setFontSize(11);
          this.drawTextWithMaxLength(header, 10, 31 + customerYIndex, doc, 190);
          doc.setFontSize(11);
          this.drawUnderline(this.translationService.translate('typeText') + specTypeValue, 15, 37 + customerYIndex, doc, 35);
        } else if (section.header === 'ntrAndAgeText') {
          doc.setFontSize(11);
          doc.text(header, 10, 100 + customerYIndex);
        }

        let counter = 0;
        section.dataPoints.forEach((dataPoint, dataIndex) => {
          const label = this.translationService.translate(dataPoint.label);

          // Split the value string into an array of keys
          const keys = dataPoint.value.split('.');

          // Use reduce to navigate through the object
          let value = keys.reduce((obj: any, key: string) => {
            if (obj && obj[key] !== undefined && obj[key] !== null) {
              return obj[key];
            } else {
              return '';
            }
          }, specLines[pageCounter]);

          // Convert value to string if it's not already a string
          if (typeof value !== 'string') {
            value = value.toString();
          }

          if (section.header === 'Specification Information') {
            let specInfoSpace = 0;
            if (label === 'CAM Spec Type') {
              specInfoSpace = 26;
            }
            let minLength = 35;
            if (label === 'CAM Spec Description') {
              minLength = 53;
            }
            doc.setFontSize(6);
            doc.text(label, 15 + dataIndex * 45 + specInfoSpace, 18 + sectionIndex * 60 + customerYIndex);
            doc.setFontSize(8);
            this.drawUnderline(value, 15 + dataIndex * 45 + specInfoSpace, 23 + sectionIndex * 60 + customerYIndex, doc, minLength);
          } else if (section.header === treadHeaderLabel) {
            if (dataIndex % 2 === 0 && dataIndex !== 0) {
              counter++;
            }
            if (specTypeValue !== this.translationService.translate('roText') || dataPoint.label !== 'treadsText') {
              const x = 15 + (dataIndex % 2) * 100;
              const y = (counter % 3) * 18 + sectionIndex * 30 + customerYIndex;
              doc.setFontSize(6);
              doc.text(label, x, 12 + y);
              doc.setFontSize(7);
              this.drawUnderline(value, x, 17 + y, doc, 85);
            }
          } else if (section.header === 'ntrAndAgeText') {
            if (dataIndex % 5 === 0 && dataIndex !== 0) {
              counter++;
            }

            doc.setFontSize(6);
            doc.text(label, 15 + (dataIndex % 5) * 25, 50 + counter * 12 + sectionIndex * 30 + customerYIndex);

            // Check if there is valid text to underline
            const textToUnderline = this.getNullSafeValue(value).toString();
            if (textToUnderline.trim().length > 0) {
              doc.setFontSize(8);
              this.drawUnderline(
                textToUnderline,
                15 + (dataIndex % 5) * 25,
                55 + counter * 12 + sectionIndex * 30 + customerYIndex,
                doc,
                dataPoint.label === 'treadsText' ? 50 : 19
              );
            }
          }
        });
      });

      let repairCounter = 0;
      let repairValueCounter = 0;
      for (let i = 0; i < specLine.limits.length; i++) {
        // Add repair limits data
        if (i > 0) {
          if (i === 1) {
            doc.addPage();
            repairCounter = 133;
            repairValueCounter = 98;
          } else {
            customerYIndex = 0;
          }
          doc.setFontSize(11);
          doc.text(
            `${this.translationService.translate('repairLimitsHeader')} ${i + 1}`,
            10,
            140 + i * 30 - repairCounter + customerYIndex
          );
        } else {
          doc.setFontSize(11);
          doc.text(
            `${this.translationService.translate('repairLimitsHeader')} ${i + 1}`,
            10,
            140 + i * 30 - repairCounter + customerYIndex
          );
        }
        doc.setFontSize(11);
        doc.text(this.translationService.translate('nailRepairHeader'), 10, 170 + i * 30 - repairCounter + customerYIndex);
        doc.text(this.translationService.translate('sectionRepairHeader'), 10, 200 + i * 30 - repairCounter + customerYIndex);
        doc.text(this.translationService.translate('otherRepairHeader'), 10, 230 + i * 30 - repairCounter + customerYIndex);

        // Setting repair limit action values
        const actionDetailsRepair = this.setActionDetails('repair', specLine.repair);
        const repairactionValue1 = actionDetailsRepair.actionValue1;
        const repairactionValue2 = actionDetailsRepair.actionValue2;
        const repairactionValue3 = actionDetailsRepair.actionValue3;
        const repairactionLabel1 = actionDetailsRepair.actionLabel1;
        const repairactionLabel2 = actionDetailsRepair.actionLabel2;
        const repairactionLabel3 = actionDetailsRepair.actionLabel3;
        this.setRepairLimitValues(
          doc,
          specLine.limits[i],
          specLine,
          repairactionValue1,
          repairactionValue2,
          repairactionValue3,
          repairactionLabel1,
          repairactionLabel2,
          repairactionLabel3,
          repairValueCounter,
          customerYIndex
        );
      }
      doc.addPage();
    }
    const docName = useCustomerInfo ? `${this.customerInfo.customerCode}-${specName}` : specName;
    doc.save(docName + '.pdf');
  }
  private getNullSafeValue(formFieldData: any) {
    return formFieldData ? formFieldData : '';
  }

  public setActionDetails(
    actionType: string,
    actionDetails: ActionDetails
  ): {
    actionValue1: string;
    actionValue2: string;
    actionValue3: string;
    actionLabel1: string;
    actionLabel2: string;
    actionLabel3: string;
  } {
    let actionValue1 = '';
    let actionValue2 = '';
    let actionValue3 = '';
    let actionLabel1 = '';
    let actionLabel2 = '';
    let actionLabel3 = '';

    if (getActionType(actionDetails.action.toUpperCase()) === ActionType.REJECT) {
      actionLabel1 = 'dispositionText';
      actionValue1 = actionType + '.disposition';
      if (getActionType(actionDetails.disposition) === DispositionType.SCRAP) {
        actionLabel2 = 'disposalCodeText';
        actionValue2 = actionType + '.disposalCode';
      }
    } else if (getActionType(actionDetails.action.toUpperCase()) === ActionType.DOWNGRADE) {
      actionLabel1 = 'applicationText';
      actionValue1 = actionType + '.application';
      actionLabel2 = 'axlesText';
      actionValue2 = actionType + '.axle';
      actionLabel3 = 'treadsText';
      actionValue3 = actionType + '.treads';
    }

    return {
      actionValue1,
      actionValue2,
      actionValue3,
      actionLabel1,
      actionLabel2,
      actionLabel3
    };
  }
  private setRepairLimitValues(
    doc: jsPDF,
    limit: Limit,
    specLine: SpecLine,
    repairactionValue1: string,
    repairactionValue2: string,
    repairactionValue3: string,
    repairactionLabel1: string,
    repairactionLabel2: string,
    repairactionLabel3: string,
    repairCounter: number,
    customerYIndex: number
  ) {
    // Example array of repair action values
    const repairactionValues = [repairactionValue1, repairactionValue2, repairactionValue3];

    // Loop through each repair action value
    repairactionValues.forEach((repairactionValue, index) => {
      // Split each repair action value into keys
      const repairactionKeys = repairactionValue.split('.');

      // Use reduce to navigate through the object
      const value = repairactionKeys.reduce((obj: any, key: string) => {
        if (obj && obj[key] !== undefined && obj[key] !== null) {
          return obj[key];
        } else {
          return '';
        }
      }, specLine);

      // Adjust coordinates based on the index to position text differently
      const xCoordinate = 69 + index * 27; // Adjust based on your layout requirements
      const yCoordinate = 153 - repairCounter + customerYIndex;

      // Check if there is valid text to draw underline
      const textToDraw = this.getNullSafeValue(value).toString();
      if (textToDraw.trim().length > 0) {
        this.drawUnderline(textToDraw, xCoordinate, yCoordinate, doc, 21);
      }
    });

    doc.setFontSize(8);
    this.drawUnderline(
      this.getNullSafeValue(limit.totalRepairLimits.totalRepairs).toString(),
      15,
      153 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(this.getNullSafeValue(specLine.repair.action), 42, 153 - repairCounter + customerYIndex, doc, 21);

    this.drawUnderline(
      this.getNullSafeValue(limit.totalRepairLimits.nailRepairs).toString(),
      15,
      183 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(
      this.getNullSafeValue(limit.totalRepairLimits.rnailCrownRepairs === 0 ? '0' : limit.totalRepairLimits.rnailCrownRepairs).toString(),
      42,
      183 - repairCounter + customerYIndex,
      doc,
      21
    );

    const rNailShoulder = limit.totalRepairLimits.rnailShoulder;
    this.drawUnderline(
      this.getNullSafeValue(rNailShoulder.repairs === 0 ? '0' : rNailShoulder.repairs).toString(),
      69,
      183 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(this.getNullSafeValue(rNailShoulder.shoulder.size).toString(), 96, 183 - repairCounter + customerYIndex, doc, 21);

    const section = limit.totalRepairLimits.section;
    this.drawUnderline(section.repairs.toString(), 15, 213 - repairCounter + customerYIndex, doc, 21);

    this.drawUnderline(this.getNullSafeValue(section.crown.count.toString()), 42, 213 - repairCounter + customerYIndex, doc, 21);
    this.drawUnderline(this.getNullSafeValue(section.crown.size).toString(), 69, 213 - repairCounter + customerYIndex, doc, 21);

    this.drawUnderline(this.getNullSafeValue(section.shoulder.count.toString()), 96, 213 - repairCounter + customerYIndex, doc, 21);
    this.drawUnderline(this.getNullSafeValue(section.shoulder.size).toString(), 123, 213 - repairCounter + customerYIndex, doc, 21);

    this.drawUnderline(this.getNullSafeValue(section.sidewall.count.toString()), 150, 213 - repairCounter + customerYIndex, doc, 21);
    this.drawUnderline(this.getNullSafeValue(section.sidewall.size).toString(), 177, 213 - repairCounter + customerYIndex, doc, 21);

    this.drawUnderline(
      this.getNullSafeValue(limit.otherRepairLimits.spot === 0 ? '0' : limit.otherRepairLimits.spot).toString(),
      15,
      243 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(
      this.getNullSafeValue(limit.otherRepairLimits.bead === 0 ? '0' : limit.otherRepairLimits.bead).toString(),
      42,
      243 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(
      this.getNullSafeValue(limit.otherRepairLimits.liner === 0 ? '0' : limit.otherRepairLimits.liner).toString(),
      69,
      243 - repairCounter + customerYIndex,
      doc,
      21
    );
    this.drawUnderline(
      this.getNullSafeValue(limit.otherRepairLimits.brake === 0 ? '0' : limit.otherRepairLimits.brake).toString(),
      96,
      243 - repairCounter + customerYIndex,
      doc,
      21
    );

    doc.setFontSize(6);
    doc.text(this.translationService.translate('totalRepairsText'), 15, 148 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('actionText'), 42, 148 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate(repairactionLabel1), 69, 148 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate(repairactionLabel2), 96, 148 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate(repairactionLabel3), 123, 148 - repairCounter + customerYIndex);

    doc.text(this.translationService.translate('totalNailText'), 15, 178 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('totalRNailCrownText'), 42, 178 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('totalRNailShoulderText'), 69, 178 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('rNailShoulderSizeText'), 96, 178 - repairCounter + customerYIndex);

    doc.text(this.translationService.translate('totalSectionText'), 15, 208 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('totalCrownSectionText'), 42, 208 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('maxCrownSizeText'), 69, 208 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('totalShoulderSectionText'), 96, 208 - repairCounter + customerYIndex);

    doc.text(this.translationService.translate('maxShoulderSizeText'), 123, 208 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('totalSidewallSectionText'), 150, 208 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('maxSidewallSizeText'), 177, 208 - repairCounter + customerYIndex);

    doc.text(this.translationService.translate('spotText'), 15, 238 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('beadText'), 42, 238 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('linerText'), 69, 238 - repairCounter + customerYIndex);
    doc.text(this.translationService.translate('brakeSkidText'), 96, 238 - repairCounter + customerYIndex);
  }
  private drawUnderline(text: string, x: number, y: number, doc: jsPDF, minLength = 0) {
    const lineHeight = 0.8; // Line height multiplier
    let fontSize = 8; // Initial font size
    const maxLines = 2; // Maximum number of lines
    const minLengthAdjusted = Math.max(minLength, 0); // Ensure minLength is not negative

    // Set font size
    doc.setFontSize(fontSize);

    // Split the text into segments by commas
    const segments = text.split(',').map((segment) => segment.trim()); // Trim spaces around each segment
    let currentLine = ''; // To accumulate segments
    let totalLines = 0; // To track total lines used

    // Function to check if the current text fits within the limit
    const fitsWithinLimit = () => {
      totalLines = 0;
      currentLine = '';

      for (const segment of segments) {
        const testLine = currentLine ? `${currentLine}, ${segment}` : segment; // Construct potential new line
        const testLineWidth = (doc.getStringUnitWidth(testLine) * fontSize) / doc.internal.scaleFactor;

        if (testLineWidth > minLengthAdjusted) {
          totalLines += 1; // Line overflow
          currentLine = segment; // Start new line
        } else {
          currentLine = testLine; // Keep adding segments to the current line
        }
      }

      // Check if there's any remaining text
      if (currentLine) {
        totalLines += 1; // Count the last line
      }

      return totalLines <= maxLines;
    };

    // Decrease font size until the text fits within the maximum line limit
    while (!fitsWithinLimit() && fontSize > 1) {
      fontSize -= 0.5; // Decrease font size by 0.5
      doc.setFontSize(fontSize); // Update the font size
    }

    // Reset the text drawing process
    currentLine = ''; // Clear current line
    totalLines = 0; // Reset line count

    // Draw the text with the adjusted font size
    for (const segment of segments) {
      const testLine = currentLine ? `${currentLine}, ${segment}` : segment; // Construct potential new line
      const testLineWidth = (doc.getStringUnitWidth(testLine) * fontSize) / doc.internal.scaleFactor;

      if (testLineWidth > minLengthAdjusted) {
        // Draw the current line if it's not empty
        if (currentLine) {
          doc.text(currentLine, x, y); // Draw text for the line
          const currentLineWidth = (doc.getStringUnitWidth(currentLine) * fontSize) / doc.internal.scaleFactor;

          // Draw underline, ensuring it meets minLength
          const underlineWidth = Math.max(currentLineWidth, minLengthAdjusted);
          doc.line(x, y + (lineHeight * fontSize) / 6, x + underlineWidth, y + (lineHeight * fontSize) / 6);

          // Move to the next line
          y += lineHeight * fontSize;
          totalLines += 1; // Increment line count
        }

        // Start a new line with the current segment
        currentLine = segment;
      } else {
        // Otherwise, keep adding segments to the current line
        currentLine = testLine;
      }
    }

    // Draw any remaining text in the current line
    if (currentLine) {
      doc.text(currentLine, x, y); // Draw the final line of text
      const remainingLineWidth = (doc.getStringUnitWidth(currentLine) * fontSize) / doc.internal.scaleFactor;

      // Draw underline for the last line, ensuring it meets minLength
      const underlineWidth = Math.max(remainingLineWidth, minLengthAdjusted);
      doc.line(x, y + (lineHeight * fontSize) / 6, x + underlineWidth, y + (lineHeight * fontSize) / 6);
    } else {
      // If no text was added, draw a line of minLength
      doc.line(x, y + (lineHeight * fontSize) / 6, x + minLengthAdjusted, y + (lineHeight * fontSize) / 6);
    }
  }

  private drawTextWithMaxLength(text: string, x: number, y: number, doc: jsPDF, maxLength: number) {
    let fontSize = 8; // Initial font size
    const minFontSize = 1; // Minimum font size

    // Set initial font size
    doc.setFontSize(fontSize);

    // Function to check if the text fits within the maxLength
    const fitsWithinMaxLength = () => {
      const textWidth = (doc.getStringUnitWidth(text) * fontSize) / doc.internal.scaleFactor;
      return textWidth <= maxLength;
    };

    // Reduce the font size until the text fits within the maxLength
    while (!fitsWithinMaxLength() && fontSize > minFontSize) {
      fontSize -= 0.5; // Decrease font size by 0.5
      doc.setFontSize(fontSize); // Update the font size
    }

    // Final check to draw text
    if (fitsWithinMaxLength()) {
      doc.text(text, x, y); // Draw the text
    } else {
      // If the text doesn't fit even with the minimum font size, handle as needed
      console.warn('Text does not fit even at the minimum font size.');
    }
  }

  generateRerunReworkReasonsPDF(rerunReworkReasons: RerunReworkReason[]) {
    // Set doc to landscape orientation
    const doc = new jsPDF({ orientation: 'landscape' });

    // Define column positions for full width usage
    // Example: [startX, width] for each column
    const columns = [
      { key: 'rerun', label: this.translationService.translate('rerunReworkReasonText'), startX: 10, width: 35 },
      { key: 'description', label: this.translationService.translate('descriptionText'), startX: 50, width: 90 }, // extra width
      { key: 'reasonType', label: this.translationService.translate('typeColumnText'), startX: 145, width: 40 },
      { key: 'cureProcess', label: this.translationService.translate('cureProcessText'), startX: 190, width: 40 },
      { key: 'active', label: this.translationService.translate('activeText'), startX: 235, width: 25 }
    ];

    // Table header
    doc.setFontSize(11);
    columns.forEach((col) => {
      doc.text(col.label, col.startX, 15);
    });

    // Table rows
    doc.setFontSize(9);
    rerunReworkReasons.forEach((reason, idx) => {
      const y = 25 + idx * 10;
      doc.text(reason.rerun || '', columns[0].startX, y, { maxWidth: columns[0].width });
      doc.text(reason.description || '', columns[1].startX, y, { maxWidth: columns[1].width });
      doc.text(reason.reasonType || '', columns[2].startX, y, { maxWidth: columns[2].width });
      doc.text(reason.cureProcess || '', columns[3].startX, y, { maxWidth: columns[3].width });
      // Display 'Yes' for 'Y', 'No' for 'N', else actual value
      let activeDisplay = reason.active === 'Y' ? 'Yes' : reason.active === 'N' ? 'No' : reason.active || '';
      doc.text(activeDisplay, columns[4].startX, y, { maxWidth: columns[4].width });
      // Add new page if needed
      if ((idx + 1) % 25 === 0 && idx !== rerunReworkReasons.length - 1) {
        doc.addPage();
      }
    });

    doc.save('RerunReworkReasons.pdf');
  }
}
